import { useState, useEffect, useCallback, useRef } from "react";
import { Step, CallBackProps, STATUS } from "react-joyride";
import {
  useTutorial,
  TutorialPageKey,
} from "../context/TutorialContext";

interface UseTutorialPageOptions {
  pageKey: TutorialPageKey;
  steps: Step[];
  // Optional delay before starting the tutorial (ms)
  delay?: number;
}

interface UseTutorialPageReturn {
  runTutorial: boolean;
  steps: Step[];
  handleJoyrideCallback: (data: CallBackProps) => void;
  startTutorial: () => void;
  skipTutorial: () => void;
}

export const useTutorialPage = ({
  pageKey,
  steps,
  delay = 500,
}: UseTutorialPageOptions): UseTutorialPageReturn => {
  const {
    tutorialEnabled,
    isNewUser,
    hasTutorialCompleted,
    markTutorialComplete,
  } = useTutorial();

  const [runTutorial, setRunTutorial] = useState(false);
  // Track which pages have started tutorials this session
  const startedPagesRef = useRef<Set<string>>(new Set());

  // Auto-start tutorial for new users who haven't completed it
  useEffect(() => {
    const hasStartedThisPage = startedPagesRef.current.has(pageKey);

    // Show tutorial if:
    // 1. Tutorials are enabled (new users on main platform)
    // 2. Haven't completed this tutorial
    // 3. Haven't already started it this session for THIS page
    // 4. There are steps to show
    const shouldShowTutorial =
      tutorialEnabled &&
      !hasTutorialCompleted(pageKey) &&
      !hasStartedThisPage &&
      steps.length > 0;

    if (shouldShowTutorial) {
      // Wait for the target elements to be mounted before starting
      const checkAndStart = () => {
        const firstStep = steps[0];
        if (firstStep && typeof firstStep.target === "string") {
          const targetEl = document.querySelector(firstStep.target);
          if (targetEl) {
            setRunTutorial(true);
            startedPagesRef.current.add(pageKey);
            return true;
          }
        }
        return false;
      };

      // Try immediately after delay, then retry a few times
      const timer = setTimeout(() => {
        if (!checkAndStart()) {
          // Retry up to 5 times with 300ms intervals
          let retries = 0;
          const retryInterval = setInterval(() => {
            retries++;
            if (checkAndStart() || retries >= 5) {
              clearInterval(retryInterval);
            }
          }, 300);
        }
      }, delay);
      return () => clearTimeout(timer);
    } else {
      // Make sure runTutorial is false when switching pages
      setRunTutorial(false);
    }
  }, [
    tutorialEnabled,
    pageKey,
    hasTutorialCompleted,
    steps.length,
    delay,
  ]);

  const handleJoyrideCallback = useCallback(
    (data: CallBackProps) => {
      const { status } = data;
      const finishedStatuses: string[] = [STATUS.FINISHED, STATUS.SKIPPED];

      if (finishedStatuses.includes(status)) {
        setRunTutorial(false);
        // Mark tutorial as complete so it won't show again
        markTutorialComplete(pageKey);
      }
    },
    [pageKey, markTutorialComplete]
  );

  const startTutorial = useCallback(() => {
    setRunTutorial(true);
  }, []);

  const skipTutorial = useCallback(() => {
    setRunTutorial(false);
    markTutorialComplete(pageKey);
  }, [pageKey, markTutorialComplete]);

  return {
    runTutorial,
    steps,
    handleJoyrideCallback,
    startTutorial,
    skipTutorial,
  };
};

export default useTutorialPage;
